import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Container,
    Grid,
    Typography,
    CircularProgress,
    Divider,
    Paper,
    useTheme
} from '@mui/material';
import {
    BarChart, Bar,
    PieChart, Pie,
    LineChart, Line,
    XAxis, YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Cell
} from 'recharts';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';

// 타입 정의
interface UserSummary {
    total: number;
    hosts: number;
    guests: number;
}

interface DailyNewUser {
    date: string;
    count: number;
}

interface DistributionItem {
    name: string;
    value: number;
}

interface ChannelDistribution {
    channel: string;
    _count: {
        channel: number;
    };
}

interface SexDistribution {
    sex: string;
    _count: {
        sex: number;
    };
}

interface LanguageDistribution {
    language: string;
    _count: {
        language: number;
    };
}

interface ConsentStatus {
    sms_ok: number;
    email_ok: number;
    alert_ok: number;
}

interface HostType {
    host_type: string;
    _count: {
        host_type: number;
    };
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string;
}

const UserStats: React.FC = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<UserSummary | null>(null);
    const [dailyNewUsers, setDailyNewUsers] = useState<DailyNewUser[]>([]);
    const [channelDistribution, setChannelDistribution] = useState<DistributionItem[]>([]);
    const [sexDistribution, setSexDistribution] = useState<DistributionItem[]>([]);
    const [languageDistribution, setLanguageDistribution] = useState<DistributionItem[]>([]);
    const [consentStatus, setConsentStatus] = useState<ConsentStatus | null>(null);
    const [hostTypes, setHostTypes] = useState<DistributionItem[]>([]);

    // 색상 팔레트 정의
    const COLORS = [
        '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe',
        '#00C49F', '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57'
    ];

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 모든 엔드포인트에서 데이터 가져오기
                const [
                    summaryData,
                    dailyData,
                    channelData,
                    sexData,
                    languageData,
                    consentData,
                    hostTypeData
                ] = await Promise.all([
                    fetch('https://roomi.co.kr/api/admin/user-stats/summary').then(res => res.json()) as Promise<UserSummary>,
                    fetch('https://roomi.co.kr/api/admin/user-stats/daily-new-users').then(res => res.json()) as Promise<DailyNewUser[]>,
                    fetch('https://roomi.co.kr/api/admin/user-stats/channel-distribution').then(res => res.json()) as Promise<ChannelDistribution[]>,
                    fetch('https://roomi.co.kr/api/admin/user-stats/sex-distribution').then(res => res.json()) as Promise<SexDistribution[]>,
                    fetch('https://roomi.co.kr/api/admin/user-stats/language-distribution').then(res => res.json()) as Promise<LanguageDistribution[]>,
                    fetch('https://roomi.co.kr/api/admin/user-stats/consent-status').then(res => res.json()) as Promise<ConsentStatus>,
                    fetch('https://roomi.co.kr/api/admin/user-stats/host-types').then(res => res.json()) as Promise<HostType[]>
                ]);

                setSummary(summaryData);
                setDailyNewUsers(dailyData);

                // 채널 데이터 처리
                const formattedChannelData: DistributionItem[] = channelData.map(item => ({
                    name: item.channel || '미지정',
                    value: item._count.channel
                }));
                setChannelDistribution(formattedChannelData);

                // 성별 데이터 처리
                const formattedSexData: DistributionItem[] = sexData.map(item => ({
                    name: formatSex(item.sex),
                    value: item._count.sex
                }));
                setSexDistribution(formattedSexData);

                // 언어 데이터 처리
                const formattedLanguageData: DistributionItem[] = languageData.map(item => ({
                    name: item.language || '미지정',
                    value: item._count.language
                }));
                setLanguageDistribution(formattedLanguageData);

                setConsentStatus(consentData);

                // 호스트 타입 데이터 처리
                const formattedHostTypeData: DistributionItem[] = hostTypeData.map(item => ({
                    name: formatHostType(item.host_type),
                    value: item._count.host_type
                }));
                setHostTypes(formattedHostTypeData);

                setLoading(false);
            } catch (err) {
                console.error('데이터 가져오기 오류:', err);
                setError('데이터를 불러오는 중 오류가 발생했습니다.');
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // 성별 포맷팅
    const formatSex = (sex: string): string => {
        if (!sex) return '미지정';
        return sex === 'M' ? '남성' : sex === 'F' ? '여성' : sex;
    };

    // 호스트 타입 포맷팅
    const formatHostType = (type: string): string => {
        if (!type) return '미지정';

        const typeMap: Record<string, string> = {
            'individual': '개인',
            'company': '기업',
            'organization': '단체'
        };

        return typeMap[type] || type;
    };

    // 날짜 포맷팅 함수
    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        return `${date.getMonth() + 1}/${date.getDate()}`;
    };

    // 커스텀 툴팁
    const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Paper sx={{ p: 1, boxShadow: 2 }}>
                    <Typography variant="body2">{`${label} : ${payload[0].value}`}</Typography>
                </Paper>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom component="div" sx={{ fontWeight: 'bold', mb: 4 }}>
                사용자 통계 대시보드
            </Typography>

            {/* 요약 정보 카드 */}
            <Box component="div" sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Card sx={{ height: '100%', bgcolor: 'primary.light', color: 'white' }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <PersonIcon sx={{ fontSize: 50, mb: 1 }} />
                                <Typography variant="h6" gutterBottom>전체 사용자</Typography>
                                <Typography variant="h4">{summary?.total.toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Card sx={{ height: '100%', bgcolor: 'success.light', color: 'white' }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <BusinessIcon sx={{ fontSize: 50, mb: 1 }} />
                                <Typography variant="h6" gutterBottom>호스트</Typography>
                                <Typography variant="h4">{summary?.hosts.toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Card sx={{ height: '100%', bgcolor: 'info.light', color: 'white' }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <PeopleAltIcon sx={{ fontSize: 50, mb: 1 }} />
                                <Typography variant="h6" gutterBottom>게스트</Typography>
                                <Typography variant="h4">{summary?.guests.toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* 최근 30일간 일별 가입자 수 */}
            <Box component="div" sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12}}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>최근 30일간 일별 가입자 수</Typography>
                                <Box sx={{ height: 300 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={dailyNewUsers.map(item => ({
                                                date: formatDate(item.date),
                                                count: item.count
                                            }))}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" />
                                            <YAxis />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="count"
                                                name="가입자 수"
                                                stroke={theme.palette.primary.main}
                                                activeDot={{ r: 8 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* 다양한 분포도 */}
            <Box component="div" sx={{ mb: 4 }}>
                <Grid container spacing={3}>
                    {/* 채널 분포 */}
                    <Grid size={{ xs: 12}}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>채널별 사용자 분포</Typography>
                                <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={channelDistribution}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {channelDistribution.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value: number) => [value, '사용자 수']} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 성별 분포 */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>성별 분포</Typography>
                                <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={sexDistribution}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {sexDistribution.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value: number) => [value, '사용자 수']} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 언어 분포 */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>언어 분포</Typography>
                                <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={languageDistribution}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {languageDistribution.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip formatter={(value: number) => [value, '사용자 수']} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* 호스트 타입 및 알림 동의 */}
            <Box component="div">
                <Grid container spacing={3}>
                    {/* 호스트 타입 비율 */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>호스트 타입 비율</Typography>
                                <Box sx={{ height: 300 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={hostTypes}
                                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            <Bar dataKey="value" name="호스트 수" fill={theme.palette.secondary.main}>
                                                {hostTypes.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* 알림 수신 동의 상태 */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>알림 수신 동의 상태</Typography>
                                <Box sx={{ height: 300 }}>
                                    {consentStatus && (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={[
                                                    { name: 'SMS', value: consentStatus.sms_ok, icon: <SmsIcon /> },
                                                    { name: '이메일', value: consentStatus.email_ok, icon: <EmailIcon /> },
                                                    { name: '앱 알림', value: consentStatus.alert_ok, icon: <NotificationsActiveIcon /> }
                                                ]}
                                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <Tooltip />
                                                <Legend />
                                                <Bar dataKey="value" name="동의 사용자 수" fill={theme.palette.info.main} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default UserStats;